/**
 * @opal/components shim — re-exports UI primitives from shadcn/ui.
 *
 * Strategy: consumer code keeps `import { ... } from "@opal/components"` unchanged.
 * This file maps every Opal API name to the shadcn/ui equivalent (or keeps a
 * minimal inline implementation for project-specific components).
 *
 * Migration status:
 *   ✅ Button       → src/components/ui/button.tsx  (shadcn cva-based)
 *   ✅ Tooltip      → src/components/ui/opal-tooltip-compat.tsx (Radix-based)
 *   ✅ Divider      → src/components/ui/separator.tsx (Radix-based)
 *   ✅ Card         → src/components/ui/card.tsx     (shadcn)
 *   ✅ Badge        → src/components/ui/badge.tsx    (shadcn cva)
 *   ⬜ Text         → inline (project-specific font tokens)
 *   ⬜ MessageCard  → inline (project-specific empty-state + alert layout)
 *   ⬜ SidebarTab   → inline (project-specific nav tab)
 *   ⬜ Table        → inline (data-driven helper, not the same as shadcn Table primitives)
 *   ⬜ SelectCard   → inline (project-specific card-button)
 *   ⬜ LineItemButton → inline (project-specific list-item button)
 *   ⬜ Input/Select → inline (thin wrappers; real shadcn inputs used elsewhere)
 */

import React, { type ReactNode, type ButtonHTMLAttributes, type HTMLAttributes } from "react";
import { Link } from "react-router-dom";
import { OpalTooltip } from "@/shared/ui/components/ui/opal-tooltip-compat";
import { Button as ShadcnButton } from "@/shared/ui/components/ui/button";

// ── shadcn/ui re-exports ────────────────────────────────────────────────────

// Button — full shadcn/cva implementation with Opal props
export {
  Button,
  buttonVariants,
  type ButtonProps,
  type ButtonVariant,
  type ButtonProminence,
} from "@/shared/ui/components/ui/button";

// Convenience button variants (backward-compat aliases)
export { Button as OpenButton } from "@/shared/ui/components/ui/button";
export { Button as LinkButton } from "@/shared/ui/components/ui/button";
export { Button as SelectButton } from "@/shared/ui/components/ui/button";
export { Button as FilterButton } from "@/shared/ui/components/ui/button";

// Tooltip — Opal single-prop API wrapping Radix Tooltip
export {
  OpalTooltip as Tooltip,
  type OpalTooltipProps as TooltipProps,
} from "@/shared/ui/components/ui/opal-tooltip-compat";

// Divider — Radix Separator (supports orientation="horizontal"|"vertical")
export { Separator as Divider } from "@/shared/ui/components/ui/separator";

// Card primitives — shadcn Card (note: @opal/layouts Card is a DIFFERENT component)
export {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
  CardTitle,
  CardDescription,
} from "@/shared/ui/components/ui/card";

// Badge — shadcn Badge (full cva variants: outline, default, destructive, etc.)
export { Badge, badgeVariants, type BadgeProps } from "@/shared/ui/components/ui/badge";

// ── Inline project-specific components ────────────────────────────────────────

// ── SelectCard ────────────────────────────────────────────────────────────────
export type SelectCardProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children"> & {
  /** "filled" = active/selected background; "empty" = dashed outline (unset). */
  state?: "filled" | "empty";
  padding?: "sm" | "md" | "lg";
  rounding?: "sm" | "md" | "lg";
  className?: string;
  children?: ReactNode;
};

export function SelectCard({
  state,
  padding = "md",
  rounding = "lg",
  className = "",
  children,
  disabled,
  type = "button",
  ...rest
}: SelectCardProps) {
  const paddings: Record<string, string> = { sm: "p-3", md: "p-4", lg: "p-6" };
  const roundings: Record<string, string> = { sm: "rounded-sm", md: "rounded-md", lg: "rounded-lg" };
  const stateCls =
    state === "filled"
      ? "bg-background-neutral-02 border border-border-01 hover:bg-background-neutral-03"
      : "bg-background-neutral-01 border border-dashed border-border-01 hover:bg-background-tint-01";
  return (
    <button
      type={type}
      disabled={disabled}
      className={[
        "w-full flex items-center gap-2 text-left transition-colors",
        "focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed",
        stateCls,
        paddings[padding] ?? paddings.md,
        roundings[rounding] ?? roundings.lg,
        className,
      ].filter(Boolean).join(" ")}
      {...rest}
    >
      {children}
    </button>
  );
}

// ── LineItemButton ─────────────────────────────────────────────────────────────
export type LineItemButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "title"> & {
  icon?: React.ElementType;
  /** Display text. Rendered as visible label (not HTML tooltip). */
  title?: string;
  state?: "selected" | "empty";
  selectVariant?: string;
  sizePreset?: string;
  variant?: string;
};

export function LineItemButton({
  icon: Icon,
  title,
  state,
  children,
  className = "",
  disabled,
  type = "button",
  selectVariant: _sv,
  sizePreset: _sp,
  variant: _v,
  ...rest
}: LineItemButtonProps) {
  const isSelected = state === "selected";
  return (
    <button
      type={type}
      disabled={disabled}
      className={[
        "w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-left transition-colors",
        "focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed",
        isSelected
          ? "bg-background-tint-01 text-text-04"
          : "text-text-03 hover:bg-background-tint-01",
        className,
      ].filter(Boolean).join(" ")}
      {...rest}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0" />}
      {(children != null || title != null) && (
        <span className="flex-1 min-w-0 truncate">{children ?? title}</span>
      )}
    </button>
  );
}

// ── Text ───────────────────────────────────────────────────────────────────────
// Kept inline: uses project-specific font token classes (font-main-ui-body,
// font-heading-h3, etc.) that shadcn doesn't know about.
interface TextProps extends HTMLAttributes<HTMLElement> {
  as?: "p" | "span" | "h1" | "h2" | "h3" | "h4" | "label" | "div";
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  weight?: "normal" | "medium" | "semibold" | "bold";
  color?: "default" | "subtle" | "muted" | "danger" | "success";
  variant?: string;
  mainUiAction?: boolean;
  headingH3?: boolean;
  text04?: boolean;
}
export function Text({
  as: Tag = "p",
  size = "md",
  weight,
  color,
  className = "",
  children,
  // Accepted-but-unused shorthand flags
  mainUiAction: _mua,
  headingH3: _h3,
  text04: _t04,
  variant: _v,
  ...rest
}: TextProps) {
  const sizes: Record<string, string> = {
    xs: "font-secondary-body",
    sm: "font-main-ui-muted",
    md: "font-main-ui-body",
    lg: "font-heading-h3-muted",
    xl: "font-heading-h2",
  };
  const weights: Record<string, string> = {
    normal: "font-normal",
    medium: "font-medium",
    semibold: "font-semibold",
    bold: "font-bold",
  };
  const colors: Record<string, string> = {
    default: "text-text-04",
    subtle: "text-text-03",
    muted: "text-text-02",
    danger: "text-red-600",
    success: "text-green-600",
  };
  const cls = [
    sizes[size] ?? "font-main-ui-body",
    weight && weights[weight],
    color && colors[color],
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return <Tag className={cls} {...rest}>{children}</Tag>;
}

// ── MessageCard ────────────────────────────────────────────────────────────────
// Kept inline: dual-mode (empty-state centered + alert/toast with severity).
type MessageCardVariant = "success" | "error" | "warning" | "info" | "default";
interface MessageCardProps {
  title?: string;
  description?: string;
  icon?: React.ElementType;
  children?: ReactNode;
  className?: string;
  variant?: MessageCardVariant;
  onClose?: () => void;
  padding?: "xs" | "sm" | "md" | "lg";
  sizePreset?: string;
  rightChildren?: React.ReactNode;
}
const MESSAGE_CARD_VARIANT_STYLES: Record<
  MessageCardVariant,
  { bg: string; border: string; icon: string }
> = {
  success: { bg: "bg-status-success-00", border: "border-status-success-02", icon: "text-status-success-05" },
  error:   { bg: "bg-status-error-00",   border: "border-status-error-02",   icon: "text-status-error-05" },
  warning: { bg: "bg-status-warning-00", border: "border-status-warning-02", icon: "text-status-warning-05" },
  info:    { bg: "bg-status-info-00",    border: "border-status-info-02",    icon: "text-status-info-05" },
  default: { bg: "bg-background-neutral-01", border: "border-border-01",     icon: "text-text-02" },
};
const MESSAGE_CARD_PADDING: Record<NonNullable<MessageCardProps["padding"]>, string> = {
  xs: "p-2", sm: "p-3", md: "p-4", lg: "p-6",
};

export function MessageCard({
  title,
  description,
  icon: Icon,
  children,
  className = "",
  variant,
  onClose,
  padding,
}: MessageCardProps) {
  if (variant) {
    const styles = MESSAGE_CARD_VARIANT_STYLES[variant];
    return (
      <div
        role="alert"
        className={`flex items-start gap-2 rounded-lg border ${styles.bg} ${styles.border} ${MESSAGE_CARD_PADDING[padding ?? "md"]} ${className}`}
      >
        {Icon && <Icon size={20} className={`shrink-0 mt-0.5 ${styles.icon}`} />}
        <div className="flex-1 min-w-0 text-left">
          {title && <p className="font-main-ui-action text-text-04">{title}</p>}
          {description && <p className="font-main-ui-body text-text-03 mt-0.5">{description}</p>}
          {children}
        </div>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Dismiss"
            className="shrink-0 text-text-03 hover:text-text-04"
          >
            ×
          </button>
        )}
      </div>
    );
  }
  return (
    <div className={`flex flex-col items-center justify-center p-8 text-center ${className}`}>
      {Icon && <Icon size={48} className="text-text-02 mb-4" />}
      {title && <h3 className="font-heading-h3 text-text-04 mb-2">{title}</h3>}
      {description && <p className="font-main-ui-muted text-text-03">{description}</p>}
      {children}
    </div>
  );
}

export function EmptyMessageCard(props: MessageCardProps) { return <MessageCard {...props} />; }

// ── SidebarTab ─────────────────────────────────────────────────────────────────
interface SidebarTabProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "type" | "onClick"> {
  onClick?: React.MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>;
  /** @deprecated use `selected` */
  active?: boolean;
  selected?: boolean;
  folded?: boolean;
  icon?: React.ElementType;
  label?: string;
  href?: string;
  target?: string;
  rightChildren?: ReactNode;
  nested?: boolean;
  variant?: "sidebar-light" | "sidebar-heavy";
  type?: "button" | "submit" | "reset";
}

export function SidebarTab({
  active,
  selected,
  folded,
  icon: Icon,
  label,
  children,
  href,
  target,
  rightChildren,
  nested,
  variant: _variant,
  disabled,
  className = "",
  onClick,
  type = "button",
  ...rest
}: SidebarTabProps) {
  const isSelected = selected ?? active;
  const text = label ?? children;
  const cls = [
    "relative flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors w-full",
    folded ? "justify-center px-2" : "",
    isSelected
      ? "bg-background-tint-02 text-text-04 font-medium"
      : "text-text-03 hover:bg-background-tint-01",
    disabled ? "opacity-50 pointer-events-none" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      {Icon ? (
        <Icon className={folded ? "h-4 w-4" : "h-4 w-4 shrink-0"} size={16} />
      ) : (
        nested && <span className="w-4" aria-hidden="true" />
      )}
      {!folded && (
        <span className="flex-1 min-w-0 truncate text-left">{text}</span>
      )}
      {!folded && rightChildren}
    </>
  );

  const element = href ? (
    <Link
      to={href}
      target={target}
      className={cls}
      aria-disabled={disabled}
      onClick={disabled ? (e) => e.preventDefault() : onClick}
    >
      {inner}
    </Link>
  ) : (
    <button
      type={type}
      className={cls}
      disabled={disabled}
      onClick={onClick}
      {...rest}
    >
      {inner}
    </button>
  );

  if (folded && text) {
    return <OpalTooltip tooltip={text}>{element}</OpalTooltip>;
  }
  return element;
}

// ── Table (data-driven helper) ─────────────────────────────────────────────────
// Note: this is a data-driven Table helper, NOT the shadcn Table primitives.
// The shadcn Table/TableHead/TableBody etc. are in src/components/ui/table.tsx.
interface TablePropsGeneric<T> { data: T[]; columns: unknown[]; className?: string; }
export function Table<T>({ data, columns, className = "" }: TablePropsGeneric<T>) {
  return (
    <div className={`overflow-auto ${className}`}>
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="border-b border-border-01">
            {(columns as Array<{ header: string }>).map((col, i) => (
              <th key={i} className="text-left px-4 py-2 font-semibold text-text-03 text-xs uppercase tracking-wide">
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, ri) => (
            <tr key={ri} className="group/row border-b border-border-01 hover:bg-background-neutral-02 transition-colors">
              {(columns as Array<{ key?: keyof T; cell?: (r: T) => ReactNode }>).map((col, ci) => (
                <td key={ci} className={`px-4 py-3 text-text-04 ${ci === (columns as unknown[]).length - 1 ? "text-right" : ""}`}>
                  {col.cell ? col.cell(row) : String((row as Record<string, unknown>)[col.key as string] ?? "")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

interface Column<T> { key?: keyof T; header: string; cell?: (row: T) => ReactNode; }
export function createTableColumns<T>(columns: Column<T>[]) { return columns; }

// ── Pagination ─────────────────────────────────────────────────────────────────
interface PaginationProps { page: number; totalPages: number; onPageChange: (page: number) => void; }
export function Pagination({ page, totalPages, onPageChange }: PaginationProps) {
  return (
    <div className="flex items-center gap-2 justify-center py-4">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="px-3 py-1 rounded border border-border-01 font-secondary-action text-text-03 disabled:opacity-50 hover:bg-background-tint-01 transition-colors"
      >
        Previous
      </button>
      <span className="font-secondary-body text-text-02">{page} / {totalPages}</span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="px-3 py-1 rounded border border-border-01 font-secondary-action text-text-03 disabled:opacity-50 hover:bg-background-tint-01 transition-colors"
      >
        Next
      </button>
    </div>
  );
}

// ── Tag ────────────────────────────────────────────────────────────────────────
// Kept inline: Opal Tag has a different variant set than shadcn Badge.
interface TagProps { label?: string; children?: ReactNode; variant?: "default" | "success" | "danger" | "warning"; }
export function Tag({ label, children, variant = "default" }: TagProps) {
  const colors = {
    default: "bg-background-tint-01 text-text-03",
    success: "bg-green-100 text-green-700",
    danger:  "bg-red-100 text-red-700",
    warning: "bg-yellow-100 text-yellow-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-secondary-action ${colors[variant]}`}>
      {label || children}
    </span>
  );
}

// ── Utilities ──────────────────────────────────────────────────────────────────
export function toPlainString(val: unknown): string {
  if (typeof val === "string") return val;
  if (!val) return "";
  return String(val);
}

// ── Additional exports ─────────────────────────────────────────────────────────

/** Simple typography wrapper */
export function Typography({
  variant = "body",
  children,
  className = "",
  ...rest
}: {
  variant?: "h1" | "h2" | "h3" | "h4" | "body" | "caption" | "label";
  children: React.ReactNode;
  className?: string;
  [k: string]: unknown;
}) {
  const tagMap: Record<string, string> = {
    h1: "h1", h2: "h2", h3: "h3", h4: "h4",
    body: "p", caption: "span", label: "span",
  };
  const Tag = (tagMap[variant] ?? "p") as React.ElementType;
  return <Tag className={className} {...rest}>{children}</Tag>;
}

/** ConfirmationButton — shows a confirm dialog before calling onClick. */
export function ConfirmationButton({
  children,
  onConfirm,
  message = "Are you sure?",
  ...rest
}: {
  children: React.ReactNode;
  onConfirm: () => void;
  message?: string;
  [k: string]: unknown;
}) {
  return (
    <ShadcnButton
      {...(rest as Record<string, unknown>)}
      onClick={() => { if (window.confirm(message)) onConfirm(); }}
    >
      {children}
    </ShadcnButton>
  );
}

/** Drawer — simple right-side panel. */
export function Drawer({
  open,
  onClose,
  children,
  title,
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1000]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute top-0 right-0 bottom-0 w-[400px] bg-white dark:bg-background-neutral-00 border-l border-border-01 p-6 overflow-y-auto shadow-2xl">
        {title && <h2 className="font-heading-h3 text-text-04 mb-4">{title}</h2>}
        {children}
      </div>
    </div>
  );
}

/** Input — thin styled wrapper (real shadcn Input is in src/components/ui/input.tsx) */
export function Input({
  className = "",
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & { className?: string }) {
  return <input className={`input ${className}`} {...rest} />;
}

/** Select — thin styled wrapper */
export function Select({
  children,
  className = "",
  ...rest
}: React.SelectHTMLAttributes<HTMLSelectElement> & { className?: string }) {
  return <select className={`select ${className}`} {...rest}>{children}</select>;
}

// ── InlineMarkdown re-export ───────────────────────────────────────────────────
// @opal/components/text/InlineMarkdown is aliased to this file via vite.config.ts
// The only actual consumer imports `toPlainString` from that path — which is
// already exported above. No separate InlineMarkdown component needed.
